import { useState } from "react";
import MemoRequestModal from "./MemoRequestModal";

const YEAR = new Date().getFullYear();

/* Marker-style highlight — soft swipe behind the lower half of the words */
const Hl = ({ children }) => (
  <span
    className="box-decoration-clone"
    style={{
      backgroundImage:
        "linear-gradient(to top, rgba(253, 224, 130, 0.35) 42%, transparent 42%)",
    }}
  >
    {children}
  </span>
);

export default function Hero() {
  const [memoOpen, setMemoOpen] = useState(false);

  return (
    <>
      <main
        className="w-full min-h-[100svh] bg-white text-[#0A0A0C] flex flex-col"
        data-testid="hero-section"
      >
        {/* Header — small logo left, single link right */}
        <header className="font-latex w-full flex items-center justify-between px-8 md:px-12 pt-8 md:pt-10">
          <a href="#" aria-label="Hadamard" className="inline-block" data-testid="nav-brand">
            <img src="/hadamard-logo.svg" alt="Hadamard" className="h-6 md:h-7 w-auto" />
          </a>
          <a
            href="mailto:hello@hadamardlabs.com"
            className="text-[13px] text-[#0A0A0C]/55 hover:text-[#0A0A0C] transition-colors duration-500"
            data-testid="nav-contact"
          >
            get in touch
          </a>
        </header>

        {/* Manifesto — narrow left-anchored column, small refined type */}
        <section className="font-latex w-full max-w-[660px] mx-auto px-8 md:px-12 pt-16 md:pt-24 pb-12 flex-1">
          <h1 className="font-display text-[18px] md:text-[19px] leading-[1.55] tracking-[-0.005em] font-light text-[#0A0A0C] mb-8 text-center">
            Hadamard is reinventing how biological systems are measured.
          </h1>

          <div className="space-y-5 text-[15px] md:text-[15.5px] leading-[1.65] text-[#0A0A0C]/70 text-justify hyphens-auto md:hyphens-none" data-testid="hero-lede">
            <p>
              Biology will remain a frontier as long as we access it through{" "}
              <Hl>last-century technologies</Hl>. Artificial intelligence,
              healthcare, and the life sciences: all these fields are bound to
              be only as good as our instruments allow them to be, since those
              instruments dictate <Hl>how and what information</Hl> we can
              access.
            </p>
            <p>
              The ground truth of biological systems lies within the systems
              themselves.
            </p>
            <p>
              Hadamard is solving this problem by building the general-purpose
              physical infrastructure for the <Hl>complete</Hl> (1),{" "}
              <Hl>accurate</Hl> (2), and <Hl>scalable</Hl> (3)&nbsp;
              <Hl>quantification</Hl> of biological systems.
            </p>
            <p>
              We currently live in a world where the available intelligence has
              completely <Hl>outgrown</Hl> the information we have.
            </p>
            <p>
              We are committed to making biological data <Hl>ordinary</Hl> and
              setting <Hl>knowledge free</Hl>.
            </p>
          </div>

          <div className="mt-12">
            <button
              type="button"
              onClick={() => setMemoOpen(true)}
              className="text-[13px] text-[#0A0A0C]/70 underline underline-offset-[6px] decoration-[#0A0A0C]/20 hover:text-[#0A0A0C] hover:decoration-[#0A0A0C]/70 transition-colors duration-500"
              data-testid="hero-cta"
            >
              → Request our memo
            </button>
          </div>
        </section>

        {/* Footer — pinned to bottom, uppercase tracked, dotted rule */}
        <footer className="font-latex w-full px-8 md:px-12 pb-6 md:pb-8 mt-auto">
          <div
            aria-hidden
            className="w-full h-px mb-4 md:mb-5"
            style={{
              backgroundImage: "radial-gradient(circle, rgba(10,10,12,0.16) 0.5px, transparent 0.5px)",
              backgroundSize: "6px 1px",
              backgroundRepeat: "repeat-x",
              backgroundPosition: "0 center",
            }}
          />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:gap-4 text-[10px] tracking-[0.16em] uppercase text-[#0A0A0C]/40">
            <span data-testid="footer-location">San Francisco · 37.77° N</span>
            <div className="flex items-center gap-4" data-testid="footer-contact">
              <a
                href="mailto:hello@hadamardlabs.com"
                className="normal-case tracking-normal text-[12px] text-[#0A0A0C]/55 hover:text-[#0A0A0C] transition-colors duration-500"
                data-testid="footer-email"
              >
                hello@hadamardlabs.com
              </a>
              <a
                href="https://www.linkedin.com/company/hadamardlabs"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-[#0A0A0C]/50 hover:text-[#0A0A0C] transition-colors duration-500"
                data-testid="footer-linkedin"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.03-1.85-3.03-1.85 0-2.13 1.45-2.13 2.94v5.66H9.37V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.26 2.37 4.26 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
                </svg>
              </a>
            </div>
            <span data-testid="footer-rights">© {YEAR} Hadamard Labs, Inc.</span>
          </div>
        </footer>
      </main>

      <MemoRequestModal open={memoOpen} onClose={() => setMemoOpen(false)} />
    </>
  );
}
